/**
 * The account's draft of changes to a rules repository (§5.27, Claude Design
 * R10) — pure, so the page draws what this says. Changes pile up in this
 * browser (`localStorage`, one draft per repository and organization or
 * workspace) until **Open pull request** sends them; nothing applies before
 * that pull request is merged. A draft that cannot be stored still works for
 * the life of the page — it is a convenience, never the record: the record is
 * the pull request.
 */
import type { RuleRfc } from '@airlock/contracts/rules/document';
import type { AccountRulesChange } from '@airlock/contracts/rules/account';

/** A statement carried from one RFC to another (kit 67): its id changes, and the pull request says so. */
export interface StatementMove {
  slug: string;
  from: number;
  to: number;
}

export type DraftChange =
  | { kind: 'put'; rfc: number; doc: RuleRfc; checks?: Record<string, string>; moved?: StatementMove[] }
  | { kind: 'delete'; rfc: number; title: string };

export interface RulesDraft {
  /** The commit the changes were written against. */
  base: string | null;
  changes: DraftChange[];
}

export const EMPTY_DRAFT: RulesDraft = { base: null, changes: [] };

export function draftKey(orgId: string, workspaceId: string | null, remote: string): string {
  return `airlock:rules-draft:${orgId}:${workspaceId ?? 'org'}:${remote}`;
}

export function loadDraft(key: string): RulesDraft {
  try {
    const raw = JSON.parse(window.localStorage.getItem(key) ?? 'null') as RulesDraft | null;
    return raw && Array.isArray(raw.changes) ? raw : EMPTY_DRAFT;
  } catch {
    return EMPTY_DRAFT;
  }
}

export function saveDraft(key: string, draft: RulesDraft): void {
  try {
    if (draft.changes.length) window.localStorage.setItem(key, JSON.stringify(draft));
    else window.localStorage.removeItem(key);
  } catch {
    /* a private window: the draft lives as long as the page */
  }
}

/**
 * The draft with one RFC written whole — replacing what the draft said of it
 * before — and the check scripts written with it, over those it kept, each one
 * a statement of it names.
 */
export function putRfc(
  draft: RulesDraft,
  doc: RuleRfc,
  base: string | null,
  checks: Record<string, string> = {},
  moved?: StatementMove[]
): RulesDraft {
  const was = draft.changes.find((one) => one.rfc === doc.rfc);
  const named = new Set(doc.statements.map((one) => one.check).filter(Boolean));
  const kept = Object.fromEntries(
    Object.entries({ ...(was?.kind === 'put' ? was.checks : {}), ...checks }).filter(([path]) => named.has(path))
  );
  const moves = moved ?? (was?.kind === 'put' ? (was.moved ?? []) : []);
  const change: DraftChange = {
    kind: 'put',
    rfc: doc.rfc,
    doc,
    ...(Object.keys(kept).length ? { checks: kept } : {}),
    ...(moves.length ? { moved: moves } : {})
  };
  return { base: draft.base ?? base, changes: [...draft.changes.filter((one) => one.rfc !== doc.rfc), change] };
}

/**
 * Keep one RFC (kit 67): written whole into the draft — or, read back exactly
 * as the branch has it with nothing else written beside it, taken out of the
 * draft: how « Undo change » on one block takes back that change alone.
 */
export function keepRfc(
  draft: RulesDraft,
  doc: RuleRfc,
  branch: RuleRfc | null,
  base: string | null,
  checks: Record<string, string> = {},
  moved?: StatementMove[]
): RulesDraft {
  const was = draft.changes.find((one) => one.rfc === doc.rfc);
  const scripts = Object.keys(checks).length || (was?.kind === 'put' && was.checks && Object.keys(was.checks).length);
  const moves = moved ?? (was?.kind === 'put' ? (was.moved ?? []) : []);
  if (branch && JSON.stringify(doc) === JSON.stringify(branch) && !scripts && !moves.length) return dropChange(draft, doc.rfc);
  return putRfc(draft, doc, base, checks, moved);
}

export type Mark = 'new' | 'changed' | 'removed' | 'moved-out' | 'moved-in';

/** The statements of an RFC's sheet, each with what the draft did to it — a removed one where it stood. */
export function sheetStatements(
  branch: RuleRfc | null,
  current: RuleRfc,
  moved: readonly StatementMove[] = []
): { slug: string; one: RuleRfc['statements'][number]; mark: Mark | null; other?: number }[] {
  const read = new Map((branch?.statements ?? []).map((one) => [one.slug, one]));
  const now = new Set(current.statements.map((one) => one.slug));
  const out: { slug: string; one: RuleRfc['statements'][number]; mark: Mark | null; other?: number }[] = current.statements.map((one) => {
    const came = moved.find((x) => x.slug === one.slug && x.to === current.rfc);
    const was = read.get(one.slug);
    return { slug: one.slug, one, mark: came ? 'moved-in' : !was ? 'new' : JSON.stringify(was) === JSON.stringify(one) ? null : 'changed', ...(came ? { other: came.from } : {}) };
  });
  (branch?.statements ?? []).forEach((one, index) => {
    if (now.has(one.slug)) return;
    const left = moved.find((x) => x.slug === one.slug && x.from === current.rfc);
    const before = branch?.statements[index - 1]?.slug;
    const at = before ? out.findIndex((x) => x.slug === before) : -1;
    out.splice(at + 1, 0, { slug: one.slug, one, mark: left ? 'moved-out' : 'removed', ...(left ? { other: left.to } : {}) });
  });
  return out;
}

/** The RFC with one statement as the branch has it again — gone again when the branch has none. */
export function undoStatement(branch: RuleRfc | null, current: RuleRfc, slug: string): RuleRfc {
  const was = branch?.statements.find((one) => one.slug === slug);
  const others = current.statements.filter((one) => one.slug !== slug);
  if (!was || !branch) return { ...current, statements: others };
  const index = branch.statements.findIndex((one) => one.slug === slug);
  const before = index > 0 ? branch.statements[index - 1].slug : null;
  const at = before ? others.findIndex((one) => one.slug === before) + 1 : 0;
  return { ...current, statements: [...others.slice(0, at), was, ...others.slice(at)] };
}

/** The RFC with its head — title, status, summary, paths, folder — as the branch has it. */
export function undoHead(branch: RuleRfc, current: RuleRfc): RuleRfc {
  const { scope: _scope, repos: _repos, ...rest } = current;
  return {
    ...rest,
    title: branch.title,
    status: branch.status,
    summary: branch.summary,
    ...(branch.scope ? { scope: branch.scope } : {}),
    ...(branch.repos?.length ? { repos: branch.repos } : {})
  };
}

/** The RFC in another folder: the projects it names, none for the top. */
export function inFolder(doc: RuleRfc, repos: readonly string[]): RuleRfc {
  const { repos: _repos, ...rest } = doc;
  return repos.length ? { ...rest, repos: [...repos].sort() } : rest;
}

/** `api+web/`, or `/` at the top. */
export function folderName(repos: readonly string[] | undefined): string {
  return repos?.length ? `${[...repos].sort().join('+')}/` : '/';
}

/** A statement carried to another RFC: both RFCs as they will be kept — or why it cannot land there. */
export function moveStatement(
  from: RuleRfc,
  to: RuleRfc,
  slug: string,
  origin: string
): { from: RuleRfc; to: RuleRfc; move: StatementMove } | { refused: string } {
  const one = from.statements.find((x) => x.slug === slug);
  if (!one || from.rfc === to.rfc) return { refused: 'It cannot be moved there.' };
  if (to.statements.some((x) => x.slug === slug))
    return { refused: `${origin}:${to.rfc} already has a statement ${slug} — rename it first, or drop it on another RFC.` };
  const left = from.statements.filter((x) => x.slug !== slug);
  if (!left.length) return { refused: `${slug} is the last statement of ${origin}:${from.rfc} — delete the RFC instead.` };
  return { from: { ...from, statements: left }, to: { ...to, statements: [...to.statements, one] }, move: { slug, from: from.rfc, to: to.rfc } };
}

/** The sentence a move is said in, in the pull request and under the statement. */
export function moveSentence(origin: string, move: StatementMove): string {
  return `${origin}:${move.from}/${move.slug} is now ${origin}:${move.to}/${move.slug} — anything citing the old id no longer finds it.`;
}

/** The list by folder: the top first, then each folder in order, each RFC by number. */
export function byFolder<T extends { rfc: RuleRfc }>(listed: readonly T[]): { name: string; repos: string[]; items: T[] }[] {
  const groups = new Map<string, T[]>();
  for (const item of listed) {
    const key = [...(item.rfc.repos ?? [])].sort().join('+');
    groups.set(key, [...(groups.get(key) ?? []), item]);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => (a === '' ? -1 : b === '' ? 1 : a.localeCompare(b)))
    .map(([key, items]) => ({ name: key ? `${key}/` : '/', repos: key ? key.split('+') : [], items }));
}

/**
 * The draft with one RFC deleted. An RFC the draft created and the repository
 * never held simply leaves the draft: there is nothing to delete on the branch.
 */
export function deleteRfc(draft: RulesDraft, rfc: number, title: string, onBranch: boolean, base: string | null): RulesDraft {
  const rest = draft.changes.filter((one) => one.rfc !== rfc);
  return onBranch ? { base: draft.base ?? base, changes: [...rest, { kind: 'delete', rfc, title }] } : { ...draft, changes: rest };
}

export function dropChange(draft: RulesDraft, rfc: number): RulesDraft {
  return { ...draft, changes: draft.changes.filter((one) => one.rfc !== rfc) };
}

/** What the page lists: the branch's RFCs as the draft leaves them, each with its mark. */
export function withDraft(rfcs: readonly RuleRfc[], draft: RulesDraft): { rfc: RuleRfc; mark: 'new' | 'edited' | 'deleted' | null }[] {
  const byNumber = new Map(draft.changes.map((one) => [one.rfc, one]));
  const listed = rfcs.map((rfc) => {
    const change = byNumber.get(rfc.rfc);
    if (!change) return { rfc, mark: null };
    return change.kind === 'delete' ? { rfc, mark: 'deleted' as const } : { rfc: change.doc, mark: 'edited' as const };
  });
  const added = draft.changes.flatMap((one) =>
    one.kind === 'put' && !rfcs.some((rfc) => rfc.rfc === one.rfc) ? [{ rfc: one.doc, mark: 'new' as const }] : []
  );
  return [...listed, ...added].sort((a, b) => a.rfc.rfc - b.rfc.rfc);
}

/** The number a new RFC is offered: the first free one after the highest, the draft's included. */
export function nextFree(rfcs: readonly RuleRfc[], draft: RulesDraft): number {
  const taken = [...rfcs.map((one) => one.rfc), ...draft.changes.map((one) => one.rfc)];
  return taken.length ? Math.max(...taken) + 1 : 1;
}

/** The statements an edit changed, added or removed, by slug. */
export function changedStatements(before: RuleRfc | null, after: RuleRfc): string[] {
  if (!before) return after.statements.map((one) => one.slug);
  const was = new Map(before.statements.map((one) => [one.slug, JSON.stringify(one)]));
  const now = new Map(after.statements.map((one) => [one.slug, JSON.stringify(one)]));
  return [...new Set([...was.keys(), ...now.keys()])].filter((slug) => was.get(slug) !== now.get(slug));
}

/** Whether a statement carried in differs from what its RFC of origin holds on the branch. */
function movedEdited(rfcs: readonly RuleRfc[], move: StatementMove, doc: RuleRfc): boolean {
  const was = rfcs.find((rfc) => rfc.rfc === move.from)?.statements.find((x) => x.slug === move.slug);
  const now = doc.statements.find((x) => x.slug === move.slug);
  return JSON.stringify(was) !== JSON.stringify(now);
}

/** The pull request's title and description, written from what changed. */
export function pullText(draft: RulesDraft, rfcs: readonly RuleRfc[], origin: string): { title: string; body: string } {
  const moves = [...new Map(draft.changes.flatMap((one) => (one.kind === 'put' ? (one.moved ?? []) : [])).map((one) => [`${one.from}/${one.slug}`, one])).values()];
  const lines = draft.changes.map((one) => {
    const before = rfcs.find((rfc) => rfc.rfc === one.rfc) ?? null;
    if (one.kind === 'delete') return `- ${origin}:${one.rfc} · ${one.title} — deleted`;
    if (!before) return `- ${origin}:${one.rfc} · ${one.doc.title} — new RFC`;
    const changed = changedStatements(before, one.doc);
    const outs = moves.filter((move) => move.from === one.rfc && changed.includes(move.slug));
    const ins = moves.filter((move) => move.to === one.rfc && changed.includes(move.slug));
    const carried = [...outs, ...ins].map((move) => move.slug);
    const edited = changed.filter((slug) => !carried.includes(slug));
    const moved = folderName(before.repos) !== folderName(one.doc.repos) ? `moved from ${folderName(before.repos)} to ${folderName(one.doc.repos)}` : null;
    const head = JSON.stringify([before.title, before.status, before.summary, before.scope]) !== JSON.stringify([one.doc.title, one.doc.status, one.doc.summary, one.doc.scope]);
    const said = [
      moved,
      ...outs.map((move) => `${move.slug} moved to ${origin}:${move.to}`),
      ...ins.map((move) => `${move.slug} moved from ${origin}:${move.from}${movedEdited(rfcs, move, one.doc) ? ' and changed' : ''}`),
      edited.length ? `${edited.join(', ')} changed` : null,
      head ? 'title, status, summary or paths changed' : null].filter(Boolean).join('; ');
    return `- ${origin}:${one.rfc} · ${one.doc.title} — ${said || 'changed'}`;
  });
  const named = draft.changes.map((one) => (one.kind === 'delete' ? `delete ${origin}:${one.rfc}` : `${origin}:${one.rfc} ${one.doc.title}`));
  const title = `Rules: ${named.slice(0, 2).join(', ')}${named.length > 2 ? ` and ${named.length - 2} more` : ''}`;
  return { title: title.slice(0, 200), body: [...lines, ...(moves.length ? ['', ...moves.map((one) => moveSentence(origin, one))] : [])].join('\n') };
}

/** What the Worker is sent. */
export function changesOf(draft: RulesDraft): AccountRulesChange[] {
  return draft.changes.map((one) =>
    one.kind === 'delete' ? { kind: 'delete', rfc: one.rfc } : { kind: 'put', rfc: one.rfc, doc: one.doc, ...(one.checks ? { checks: one.checks } : {}) }
  );
}

/** Whether a statement matches the search and the level and status filters. */
export function statementMatches(
  rfc: RuleRfc,
  statement: RuleRfc['statements'][number],
  q: string,
  levels: readonly string[],
  statuses: readonly string[]
): boolean {
  const words = q.trim().toLowerCase();
  if (words && ![statement.slug, statement.text, statement.why ?? '', rfc.title].some((one) => one.toLowerCase().includes(words))) return false;
  if (levels.length && !levels.includes(statement.level)) return false;
  if (statuses.length && !statuses.includes(statement.status ?? rfc.status)) return false;
  return true;
}
